import { useCallback, useEffect, useState } from "react";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import UoMMasterTable from "../components/UoMMasterTable";
import UoMMasterForm from "../components/UoMMasterForm";
import UoMMasterDetailView from "../components/UoMMasterDetailView";
import { UoMMaster, CreateUoMMasterData, uomMasterService } from "../../../services/uomMasterService";

const ITEMS_PER_PAGE = 6;

export default function UoMMasters() {
  const [uomMasters, setUoMMasters] = useState<UoMMaster[]>([]);
  const [totalUoMMasters, setTotalUoMMasters] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingUoMMaster, setEditingUoMMaster] = useState<UoMMaster | null>(null);
  const [viewingUoMMaster, setViewingUoMMaster] = useState<UoMMaster | null>(null);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUoMMasters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        page: number;
        limit: number;
        sortBy: string;
        search?: string;
        category?: string;
        isActive?: boolean;
      } = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: `${sortField}:${sortOrder}`,
      };

      if (search) params.search = search;
      if (category) params.category = category;
      if (isActive !== null) params.isActive = isActive;

      const result = await uomMasterService.getUoMMasters(params);
      
      if ('meta' in result) {
        // Paginated response
        setUoMMasters(result.data);
        setTotalUoMMasters(result.meta?.totalDocs || 0);
      } else {
        // Single record or array response
        if (Array.isArray(result.data)) {
          setUoMMasters(result.data);
          setTotalUoMMasters(result.data.length);
        } else {
          setUoMMasters([result.data]);
          setTotalUoMMasters(1);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch UOM", err);
      setError(err.message || "Failed to fetch UOM");
      setUoMMasters([]);
      setTotalUoMMasters(0);
    } finally {
      setLoading(false);
    }
  }, [search, category, isActive, page, sortField, sortOrder]);

  useEffect(() => {
    fetchUoMMasters();
  }, [fetchUoMMasters]);

  const handleAdd = async (uomMasterData: CreateUoMMasterData) => {
    try {
      if (editingUoMMaster && editingUoMMaster._id) {
        // Update existing UOM
        await uomMasterService.updateUoMMaster(editingUoMMaster._id, uomMasterData);
      } else {
        // Create new UOM
        await uomMasterService.createUoMMaster(uomMasterData);
      }
      setEditingUoMMaster(null);
      setOpenForm(false);
      // Refetch data from backend to ensure consistency
      fetchUoMMasters();
    } catch (error: any) {
      console.error("Failed to save UOM:", error);
      setError(error.message || "Failed to save UOM");
      throw error; // Re-throw to let UoMMasterForm handle the error display
    }
  };

  const handleView = (uomMaster: UoMMaster) => {
    setViewingUoMMaster(uomMaster);
    setShowDetailView(true);
  };

  const handleEdit = (uomMaster: UoMMaster) => {
    setEditingUoMMaster(uomMaster);
    setOpenForm(true);
  };

  const handleToggleStatus = async (uomMaster: UoMMaster) => {
    try {
      if (uomMaster._id) {
        if (uomMaster.isActive) {
          await uomMasterService.inactivateUoMMaster(uomMaster._id);
        } else {
          await uomMasterService.activateUoMMaster(uomMaster._id);
        }
        // Refresh to get updated data
        fetchUoMMasters();
      }
    } catch (error: any) {
      console.error("Failed to toggle UOM status:", error);
      setError(error.message || "Failed to toggle UOM status");
    }
  };


  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      {/* Page Header */}
      <PageBreadcrumb pageTitle="UOM" />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* UOM Table */}
      <UoMMasterTable
        uomMasters={uomMasters}
        search={search}
        setSearch={setSearch}
        category={category}
        setCategory={setCategory}
        isActive={isActive}
        setIsActive={setIsActive}
        page={page}
        setPage={setPage}
        totalUoMMasters={totalUoMMasters}
        onEdit={handleEdit}
        onView={handleView}
        onToggleStatus={handleToggleStatus}
        onAdd={() => {
          setEditingUoMMaster(null);
          setOpenForm(true);
        }}
        onRefresh={fetchUoMMasters}
        sortField={sortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        loading={loading}
      />

      {/* UOM Form Drawer */}
      <RightDrawer
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditingUoMMaster(null);
        }}
        size="60"
      >
        <div className="p-1">
          <UoMMasterForm
            editingUoMMaster={editingUoMMaster}
            onAdd={handleAdd}
            onClose={() => {
              setOpenForm(false);
              setEditingUoMMaster(null);
            }}
          />
        </div>
      </RightDrawer>

      {/* UOM Detail View Drawer */}
      <RightDrawer
        open={showDetailView}
        onClose={() => {
          setShowDetailView(false);
          setViewingUoMMaster(null);
        }}
        size="2xl"
      >
        <div className="h-full">
          <UoMMasterDetailView uomMaster={viewingUoMMaster} />
        </div>
      </RightDrawer>
    </div>
  );
}
